/**
 * Worklist row badge slot — renders a stack of plugin badges inside a
 * single worklist row. One badge per plugin that declares the
 * `worklist_row_badge` slot in its manifest.
 *
 * Rendering is CATEGORY-driven, not vendor-driven. Each plugin category
 * (triage, utility, ...) has a canonical badge appearance; the plugin
 * supplies DATA (result JSON) and the host supplies PRESENTATION. Vendors
 * don't ship React components — that would require dynamic module loading
 * with its security and bundling implications. The manifest controls look
 * via `complianceStrings` and the result-status set, which is enough for
 * every known-ahead-of-time use case.
 */

import type { CSSProperties } from 'react';
import { usePluginResult, usePluginsForSlot } from '../hooks';
import type { LoadedRuntimePlugin } from '../types';

interface Props {
  studyId: string;
}

export function WorklistBadgeSlot({ studyId }: Props) {
  const plugins = usePluginsForSlot('worklist_row_badge');
  if (plugins.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      {plugins.map((p) => (
        <PluginBadge key={p.manifest.id} plugin={p} studyId={studyId} />
      ))}
    </span>
  );
}

function PluginBadge({
  plugin,
  studyId,
}: {
  plugin: LoadedRuntimePlugin;
  studyId: string;
}) {
  // Poll every 2s while pending — once a result lands the hook stops.
  const { data } = usePluginResult(plugin, studyId, 2000);
  if (!data || data.status === 'pending') {
    return <SkeletonBadge label={plugin.manifest.name} />;
  }
  if (data.status === 'skipped') {
    return <SmallBadge color="#9ca3af" title={`${plugin.manifest.name}: ${data.skipped_reason ?? 'skipped'}`}>—</SmallBadge>;
  }
  if (data.status === 'error') {
    return <SmallBadge color="#ef4444" title={`${plugin.manifest.name}: ${data.error ?? 'error'}`}>!</SmallBadge>;
  }
  // Status = success.
  if (plugin.manifest.category === 'triage' && data.zone) {
    return <TriageBadge zone={data.zone} score={data.score} action={data.action} vendor={plugin.manifest.name} />;
  }
  // Fallback for utility / future categories.
  return <SmallBadge color="#3b82f6" title={`${plugin.manifest.name}: ${data.status}`}>•</SmallBadge>;
}

// ---------- category-specific renderings ----------

const ZONE_COLORS: Record<string, string> = {
  Normal: '#16a34a',       // green
  Uncertain: '#f59e0b',    // amber
  Abnormal: '#dc2626',     // red
};

function TriageBadge({
  zone,
  score,
  action,
  vendor,
}: {
  zone: string;
  score?: number;
  action?: string;
  vendor: string;
}) {
  const color = ZONE_COLORS[zone] ?? '#6b7280';
  const scoreStr = typeof score === 'number' ? ` ${score.toFixed(2)}` : '';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: 10,
        background: color,
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '14px',
      }}
      title={`${vendor} · ${zone}${scoreStr}${action ? ` · ${action}` : ''}`}
    >
      {zone[0]}{scoreStr}
    </span>
  );
}

function SkeletonBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: 10,
        background: '#e5e7eb',
        color: '#9ca3af',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '14px',
      }}
      title={`${label}: analyzing…`}
    >
      …
    </span>
  );
}

function SmallBadge({
  children,
  color,
  title,
}: {
  children: string;
  color: string;
  title: string;
}) {
  const style: CSSProperties = {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: 10,
    background: color,
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '14px',
  };
  return (
    <span style={style} title={title}>
      {children}
    </span>
  );
}
